import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { batchActions } from "@obg/ngrx-utils";
import {
	BettingLimitAction,
	BettingLimitSelector,
	BettingLimitType,
	CommonState,
	IauiBettingLimitAction,
	IauiBettingLimitSelector,
	IauiVerificationSelector,
} from "@obg/ngx.common";
import { AbstractTranslatableAlertService, AuthSelector, Jurisdiction, JurisdictionSelector } from "@obg/ngx.core";
import { TranslationGroup } from "@obg/ngx.core.content";
import { ignoreNil } from "@obg/rxjs";
import { combineLatest, forkJoin, merge, Observable } from "rxjs";
import { filter, map, pairwise, switchMap, take, tap } from "rxjs/operators";

import { MODAL_ID } from "./iaui-betting-limit.const";
import { IauiLoginBettingLimitModalComponent } from "./iaui-login-betting-limit-modal.component";

@Injectable({
	providedIn: "root",
})
export class IauiBettingLimitService {
	private afterLogin$ = this.store.select(AuthSelector.isAuthenticated).pipe(
		pairwise(),
		filter(([prev, next]) => !prev && next)
	);

	constructor(
		private readonly store: Store<CommonState>,
		private readonly iauiVerificationSelector: IauiVerificationSelector,
		private readonly translatableAlertService: AbstractTranslatableAlertService
	) {}

	initialize$() {
		const fetchBettingLimits$ = this.store.select(JurisdictionSelector.get).pipe(
			filter((jurisdiction) => jurisdiction === Jurisdiction.Iaui),
			switchMap(() => this.afterLogin$),
			tap(() => {
				this.store.dispatch(
					batchActions(
						BettingLimitAction.getBetCountLimit(),
						BettingLimitAction.getBetSizeLimit(),
						BettingLimitAction.getWageringLimit()
					)
				);
			})
		);

		const showPopup$ = forkJoin([
			this.store.select(this.iauiVerificationSelector.isVerificationInProgress()),
			this.store.select(this.iauiVerificationSelector.shouldShowVerificatonNotification$()),
		]).pipe(
			map(([isInProgress, shouldShow]) => {
				if (!isInProgress && shouldShow) {
					this.store.dispatch(IauiBettingLimitAction.openModal());
				}
			})
		);

		return merge(showPopup$, fetchBettingLimits$, this.initPopup$());
	}

	initPopup$(): Observable<void> {
		return combineLatest([
			this.store.select(IauiBettingLimitSelector.isModalOpen),
			this.store.select(JurisdictionSelector.get).pipe(ignoreNil()),
		]).pipe(
			filter(([isOpen, jurisdiction]) => isOpen && jurisdiction === Jurisdiction.Iaui),
			switchMap(() => this.hasObsoleteBettingLimit$()),
			filter((obsolete) => !!obsolete),
			map(() => {
				this.translatableAlertService.open$(
					{
						id: MODAL_ID,
						title: "iaui.betting-limit.mandatory-update_title",
						hideClose: true,
						dialogConfig: {
							disableClose: true,
						},
						messageComponent: IauiLoginBettingLimitModalComponent,
					},
					[TranslationGroup.Common]
				);
			})
		);
	}

	private hasObsoleteBettingLimit$(): Observable<boolean> {
		const obsoleteBettingLimitTypes = [BettingLimitType.BetCountLimit];

		return this.store.select(AuthSelector.isAuthenticated).pipe(
			filter((isAuth) => !!isAuth),
			switchMap(() =>
				combineLatest([
					this.store.select(BettingLimitSelector.getActiveBettingLimit),
					this.store.select(BettingLimitSelector.getQueuedBettingLimit),
				])
			),
			filter(([activeBettingLimit]) => !!activeBettingLimit),
			take(1),
			map(([activeBettingLimit, queuedBettingLimit]) => {
				const hasQueuedBettingLimit = !!queuedBettingLimit;
				const isActiveBettingLimitObsolete = obsoleteBettingLimitTypes.includes(activeBettingLimit!.type);
				const isQueuedBettingLimitObsolete =
					queuedBettingLimit?.type && obsoleteBettingLimitTypes.includes(queuedBettingLimit?.type);

				return isQueuedBettingLimitObsolete || (!hasQueuedBettingLimit && isActiveBettingLimitObsolete);
			})
		);
	}
}
